import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ForecastComparisonRequest } from '@futrli-sdk/predict';

import { areMultiForecastCompQueriesEqual } from '../../../utils-data';
import { useReportBuilderContext } from '../../../report-builder.context';
import { LayoutData, WIDGET_TYPES } from '../../../types';
import { useI18nConstants } from '../../../use-i18n-constants';

interface ComparisonQuery {
  i: string;
  type: WIDGET_TYPES | undefined;
  query: ForecastComparisonRequest | undefined;
}

export const useLatestComparisonQueries = () => {
  const {
    getWidgetType,
    currentCard,
    currentCardId,
    allFinFields,
    forecasts,
    isTemplate,
    isWorkspaceTemplate,
    financialConfig,
    reportData,
    updateComparisonDataForWidget,
    emptyComparisonDataForWidget,
    rowFormulaData,
    futrliFormulaData
  } = useReportBuilderContext();
  const { getComparisonQueryForWidget, actualForecasts } = useI18nConstants();

  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const widgetType = currentCardId ? getWidgetType() : undefined;
  const initialCardData = useMemo(() => reportData.pageLayoutData.find(e => e.i === currentCard?.i), [
    reportData,
    currentCard
  ]);

  const getLatestComparisonQuery = useCallback(
    (cardData?: LayoutData) => {
      if (cardData) {
        return getComparisonQueryForWidget(
          cardData,
          allFinFields,
          [...(rowFormulaData || []), ...(futrliFormulaData || [])],
          [...forecasts, ...actualForecasts],
          isFutrliTemplate,
          financialConfig
        );
      }
    },
    [
      actualForecasts,
      allFinFields,
      financialConfig,
      forecasts,
      futrliFormulaData,
      getComparisonQueryForWidget,
      isFutrliTemplate,
      rowFormulaData
    ]
  );

  const prevQuery = useRef<ComparisonQuery>({
    i: currentCard?.i || '',
    type: widgetType,
    query: getLatestComparisonQuery(initialCardData)
  });

  // Set latest datasets comparison query in context to force data to load
  useEffect(() => {
    if (!currentCard) {
      return;
    }

    const newQuery = getLatestComparisonQuery(currentCard);

    // A widget only produces a comparison query while it is a table. When the widget is
    // switched to another type we keep the comparison data we already hold (the widget
    // simply ignores it) so switching back to a table renders instantly without refetching.
    // `prevQuery` therefore tracks the query the data we hold belongs to.
    if (!newQuery && widgetType !== WIDGET_TYPES.TABLE) {
      return;
    }

    const hasCurrentCardChanged = prevQuery.current.i !== currentCard.i;
    const hasQueryDataChanged = !areMultiForecastCompQueriesEqual(newQuery, prevQuery.current.query);

    if (hasCurrentCardChanged) {
      prevQuery.current = {
        i: currentCard.i,
        type: widgetType,
        query: getLatestComparisonQuery(initialCardData)
      };
    } else if (hasQueryDataChanged) {
      prevQuery.current = { i: currentCard.i, type: widgetType, query: newQuery };
      if (newQuery) {
        updateComparisonDataForWidget(currentCard.i, newQuery);
      } else {
        emptyComparisonDataForWidget(currentCard.i);
      }
    }
  }, [
    currentCard,
    emptyComparisonDataForWidget,
    getLatestComparisonQuery,
    initialCardData,
    updateComparisonDataForWidget,
    widgetType
  ]);
};
